// 📄 components/proyecto/GeneralStatsView.tsx
// Vista "General" del panel de estadísticas: dona de progreso de
// configuración global + metric cards (SR/Issues/Interfaces) + avance por
// pestaña de configuración general. Extraído tal cual de ProjectStatsPanel.tsx
// al agregar las pestañas por solución — sin cambios visuales ni de lógica.
//
// `stats` viene de ProjectStatsPanel.tsx (que llama a useProjectStats una
// sola vez, siempre) en vez de pedirlos acá: así la barra comprimida del
// panel puede mostrar los mismos datos sin una segunda lectura a Firestore.
import React from 'react';
import { Ticket, AlertTriangle, Plug } from 'lucide-react';
import DonutChart from '../common/DonutChart.tsx';
import MetricCard from '../common/MetricCard.tsx';
import ProgressRow from '../common/ProgressRow.tsx';
import { ProjectStats } from '../../hooks/useProjectStats.ts';

interface GeneralStatsViewProps {
  stats: ProjectStats;
  ambienteActivo?: string;
}

const COLOR_COMPLETADO = '#2563eb';
const COLOR_PENDIENTE = '#4b5563';

export default function GeneralStatsView({ stats, ambienteActivo = 'DEV' }: GeneralStatsViewProps) {
  const {
    loading,
    error,
    totalPasos,
    pasosCompletados,
    pasosPendientes,
    porcentaje,
    srCount,
    issuesAbiertosCount,
    interfacesCount,
    avancePorTab
  } = stats;

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6 animate-pulse">
        <div className="w-[140px] h-[140px] rounded-full bg-white/10 mx-auto lg:mx-0" />
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="h-20 bg-white/10 rounded-xl" />
            <div className="h-20 bg-white/10 rounded-xl" />
            <div className="h-20 bg-white/10 rounded-xl" />
          </div>
          <div className="h-24 bg-white/10 rounded-xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-gray-500 text-center py-6">
        No se pudieron cargar las estadísticas.
      </div>
    );
  }

  const segments = [
    { label: 'Completado', value: pasosCompletados, color: COLOR_COMPLETADO },
    { label: 'Pendiente', value: pasosPendientes, color: COLOR_PENDIENTE }
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6">
      {/* Columna izquierda: dona */}
      <div className="flex flex-col items-center lg:items-start gap-3">
        <p className="text-sm text-gray-400">Progreso de configuración</p>
        <DonutChart
          segments={segments}
          centerLabel={`${porcentaje}%`}
          centerSublabel={totalPasos > 0 ? `${pasosCompletados} de ${totalPasos}` : 'Sin pasos'}
        />
        <div className="flex flex-col gap-1 text-xs text-gray-400">
          {segments.map((s) => (
            <span key={s.label} className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
              {s.label} {s.value}
            </span>
          ))}
        </div>
      </div>

      {/* Columna derecha: métricas + avance por pestaña */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <MetricCard icon={Ticket} label="SR creadas" value={srCount} />
          <MetricCard icon={AlertTriangle} label="Issues abiertos" value={issuesAbiertosCount} variant="danger" />
          <MetricCard icon={Plug} label="Interfaces activas" value={interfacesCount} />
        </div>

        <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
          <p className="text-sm text-gray-400">Avance por pestaña · ambiente {ambienteActivo}</p>
          {avancePorTab.length === 0 && (
            <p className="text-xs text-gray-500">Sin configuraciones registradas.</p>
          )}
          {avancePorTab.map((tab) => (
            <ProgressRow key={tab.tab} label={tab.tab} completed={tab.completados} total={tab.total} />
          ))}
        </div>
      </div>
    </div>
  );
}
